import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

/**
 * Time series chart for aggregated ad groups (clusters, types, features, etc.)
 * Shows performance trends for groups of ads rather than individual ads
 */
const GroupTimeSeriesChart = ({ accountId, groups = [], dateRangeDays = 7, numberOfPeriods = 14, groupType = 'group', startDate = null, endDate = null }) => {
  const [selectedMetric, setSelectedMetric] = useState('roas');
  const [chartDataByGroup, setChartDataByGroup] = useState({}); // Store data for each group
  const [visibleGroups, setVisibleGroups] = useState(new Set()); // Track which groups are visible
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Available metrics
  const metrics = [
    { key: 'roas', label: 'ROAS', format: (val) => `${val.toFixed(2)}x`, color: '#3b82f6' },
    { key: 'total_spend', label: 'Spend', format: (val) => `$${val.toLocaleString()}`, color: '#ef4444' },
    { key: 'total_revenue', label: 'Revenue', format: (val) => `$${val.toLocaleString()}`, color: '#10b981' },
    { key: 'ctr', label: 'CTR', format: (val) => `${val.toFixed(2)}%`, color: '#8b5cf6' },
    { key: 'cpm', label: 'CPM', format: (val) => `$${val.toFixed(2)}`, color: '#f59e0b' },
    { key: 'cpa', label: 'CPA', format: (val) => `$${val.toFixed(2)}`, color: '#ec4899' },
    { key: 'total_impressions', label: 'Impressions', format: (val) => val.toLocaleString(), color: '#6366f1' },
    { key: 'total_clicks', label: 'Clicks', format: (val) => val.toLocaleString(), color: '#14b8a6' },
    { key: 'total_purchases', label: 'Conversions', format: (val) => val.toLocaleString(), color: '#f97316' },
    { key: 'hook_rate', label: 'Hook Rate', format: (val) => `${val.toFixed(1)}%`, color: '#e11d48' },
    { key: 'hold_rate', label: 'Hold Rate', format: (val) => `${val.toFixed(1)}%`, color: '#db2777' }
  ];

  // Generate colors for groups
  const generateColors = (count) => {
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899',
      '#6366f1', '#14b8a6', '#f97316', '#e11d48', '#db2777', '#06b6d4',
      '#84cc16', '#f43f5e', '#a855f7', '#64748b', '#0ea5e9', '#22c55e'
    ];
    return colors.slice(0, count);
  };

  // Initialize visible groups (all visible by default)
  useEffect(() => {
    if (groups.length > 0 && visibleGroups.size === 0) {
      const allGroupNames = new Set(groups.map(g => g.name));
      setVisibleGroups(allGroupNames);
    }
  }, [groups, visibleGroups.size]);

  // Fetch time series data for all groups
  useEffect(() => {
    if (groups.length === 0 || !accountId) return;

    const fetchAllGroupsData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const fetchPromises = groups
          .filter(group => group.adIds && group.adIds.length > 0)
          .map(async (group) => {
            try {
              const requestBody = {
                accountId,
                adIds: group.adIds,
                groupName: group.name,
                dateRangeDays,
                numberOfPeriods
              };
              
              // Add custom date range if provided
              if (startDate && endDate) {
                requestBody.startDate = startDate;
                requestBody.endDate = endDate;
              }
              
              const response = await axios.post('/api/time-series/ad-group-performance-history', requestBody);
              
              if (response.data.success) {
                return { groupName: group.name, data: response.data.data };
              }
              return null;
            } catch (err) {
              console.error(`Error fetching data for ${group.name}:`, err);
              return null;
            }
          });

        const results = await Promise.all(fetchPromises);
        const dataMap = {};
        results.forEach(result => {
          if (result) {
            dataMap[result.groupName] = result.data;
          }
        });
        
        setChartDataByGroup(dataMap);
      } catch (err) {
        console.error('Error fetching time series data:', err);
        setError(err.response?.data?.error || 'Failed to load chart data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllGroupsData();
  }, [groups, accountId, dateRangeDays, numberOfPeriods, startDate, endDate]);

  // Toggle group visibility
  const toggleGroup = (groupName) => {
    setVisibleGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  if (!groups || groups.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Performance Trends by {groupType}</h3>
        </div>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No {groupType}s available for time series analysis</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Performance Trends by {groupType}</h3>
        </div>
        <div className="text-xs text-gray-500">
          {startDate && endDate ? (
            (() => {
              const start = new Date(startDate);
              const end = new Date(endDate);
              const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
              // Period size = total days in custom range (each period is same size as the range)
              return `Last ${numberOfPeriods} periods (${totalDays} days each)`;
            })()
          ) : (
            `Last ${numberOfPeriods} periods (${dateRangeDays} days each)`
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="mb-6">
        {/* Metric Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Metric
          </label>
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            {metrics.map((metric) => (
              <option key={metric.key} value={metric.key}>
                {metric.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Chart */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        )}
        
        {error && (
          <div className="flex items-center justify-center h-64 text-red-500">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-300" />
              <p>{error}</p>
            </div>
          </div>
        )}
        
        {!loading && !error && (() => {
          // Merge all group data into a single dataset
          // Use period number from data for proper chronological sorting
          const periodMap = new Map(); // period number -> periodLabel
          Object.values(chartDataByGroup).forEach(data => {
            data.forEach(d => {
              if (d.period !== undefined && d.periodLabel) {
                periodMap.set(d.period, d.periodLabel);
              }
            });
          });
          
          // Sort periods by period number (descending - oldest first, matching backend's reverse())
          // Backend returns data reversed, so period 13 (oldest) comes first, period 0 (most recent) comes last
          const sortedPeriods = Array.from(periodMap.entries())
            .sort((a, b) => b[0] - a[0]) // Sort by period number descending
            .map(([period, label]) => label);
          
          // Create mapping to preserve full group names (outside map so it's accessible in tooltip)
          const groupNameMap = {}; // dataKey -> full group name
          Object.keys(chartDataByGroup).forEach(groupName => {
            if (visibleGroups.has(groupName)) {
              const dataKey = `${groupName}|||${selectedMetric}`;
              groupNameMap[dataKey] = groupName; // Store mapping once
            }
          });
          
          // Create merged dataset
          const mergedData = sortedPeriods.map(period => {
            const dataPoint = { periodLabel: period };
            Object.keys(chartDataByGroup).forEach(groupName => {
              if (visibleGroups.has(groupName)) {
                const groupData = chartDataByGroup[groupName];
                const periodData = groupData.find(d => d.periodLabel === period);
                if (periodData) {
                  // Use a separator that's unlikely to appear in group names
                  const dataKey = `${groupName}|||${selectedMetric}`;
                  dataPoint[dataKey] = parseFloat(periodData[selectedMetric] || 0);
                }
              }
            });
            return dataPoint;
          });

          if (mergedData.length === 0 || visibleGroups.size === 0) {
            return (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No historical data available</p>
                </div>
              </div>
            );
          }

          // Calculate max and min values for proper scaling
          const allValues = mergedData.flatMap(d => 
            Object.keys(d)
              .filter(key => key !== 'periodLabel')
              .map(key => parseFloat(d[key] || 0))
          );
          const maxValue = Math.max(...allValues);
          const minValue = Math.min(...allValues);
          
          // Calculate domain with some padding
          const padding = (maxValue - minValue) * 0.1 || maxValue * 0.1 || 1;
          const yAxisDomain = [Math.max(0, minValue - padding), maxValue + padding];
          
          // Format Y-axis dynamically based on max value
          const formatYAxis = (value) => {
            if (selectedMetric === 'total_spend' || selectedMetric === 'total_revenue') {
              if (maxValue >= 1000000) {
                return `$${(value / 1000000).toFixed(1)}M`;
              } else if (maxValue >= 1000) {
                return `$${(value / 1000).toFixed(1)}k`;
              } else {
                return `$${value.toFixed(0)}`;
              }
            }
            if (selectedMetric === 'total_impressions' || selectedMetric === 'total_clicks' || selectedMetric === 'total_purchases') {
              if (maxValue >= 1000000) {
                return `${(value / 1000000).toFixed(1)}M`;
              } else if (maxValue >= 1000) {
                return `${(value / 1000).toFixed(1)}k`;
              } else {
                return `${value.toFixed(0)}`;
              }
            }
            return value.toFixed(1);
          };

          // Format X-axis with date ranges
          const formatXAxis = (periodLabel) => {
            return periodLabel;
          };

          const colors = generateColors(groups.length);
          const visibleGroupsList = Array.from(visibleGroups);

          return (
            <>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={mergedData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="periodLabel" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatXAxis}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    stroke="#6b7280"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                    tickFormatter={formatYAxis}
                    domain={yAxisDomain}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        // Sort payload by value (high to low)
                        const sortedPayload = [...payload].sort((a, b) => {
                          const aValue = parseFloat(a.value || 0);
                          const bValue = parseFloat(b.value || 0);
                          return bValue - aValue; // Descending order
                        });
                        
                        return (
                          <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                            <p className="font-medium text-gray-900 mb-2">{label}</p>
                            {sortedPayload.map((entry, index) => {
                              // Extract group name from dataKey (format: "groupName|||metric")
                              const groupName = groupNameMap[entry.dataKey] || entry.dataKey.split('|||')[0] || entry.dataKey.split('_')[0];
                              const metric = metrics.find(m => m.key === selectedMetric);
                              return (
                                <p key={index} className="text-sm text-gray-700" style={{ color: entry.color }}>
                                  <span className="font-semibold">{groupName}:</span>{' '}
                                  {metric.format(parseFloat(entry.value || 0))}
                                </p>
                              );
                            })}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  {visibleGroupsList.map((groupName, index) => {
                    const group = groups.find(g => g.name === groupName);
                    const color = colors[index % colors.length];
                    const dataKey = `${groupName}|||${selectedMetric}`;
                    return (
                      <Line
                        key={groupName}
                        type="monotone"
                        dataKey={dataKey}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ fill: color, strokeWidth: 2, r: 3 }}
                        activeDot={{ r: 5 }}
                        name={groupName}
                        connectNulls
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
              
              {/* Toggle buttons for groups */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {groups.map((group, index) => {
                    const isVisible = visibleGroups.has(group.name);
                    const color = colors[index % colors.length];
                    return (
                      <button
                        key={group.name}
                        onClick={() => toggleGroup(group.name)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isVisible
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        style={isVisible ? { borderLeft: `4px solid ${color}` } : {}}
                      >
                        {group.name} {group.adIds ? `(${group.adIds.length})` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default GroupTimeSeriesChart;

